
import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion as Motion, AnimatePresence } from "framer-motion";

export default function AvatarCarousel({
  avatars = [],
  autoRotate = true,
  rotateInterval = 10000,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState(0); // -1: left, 1: right
  const timerRef = useRef(null);
  const touchStartX = useRef(null);

  // Auto-rotation logic
  useEffect(() => {
    if (!autoRotate || isPaused || avatars.length === 0) return;
    timerRef.current = setInterval(() => {
      handleNext();
    }, rotateInterval);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line
  }, [autoRotate, isPaused, currentIndex, rotateInterval, avatars.length]);

  // Slide to next avatar
  const handleNext = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % avatars.length);
  }, [avatars.length]);

  // Slide to previous avatar
  const handlePrev = useCallback(() => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + avatars.length) % avatars.length);
  }, [avatars.length]);

  // Go to specific avatar
  const handleAvatarClick = (index) => {
    if (index === currentIndex) return;
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 2000);
  };

  // Pause auto-rotation on hover
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setTimeout(() => setIsPaused(false), 2000);

  // Touch events for swipe
  const handleTouchStart = (e) => {
    if (e.touches && e.touches.length === 1) {
      touchStartX.current = e.touches[0].clientX;
    }
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const endX = e.changedTouches[0].clientX;
    const diff = endX - touchStartX.current;
    if (Math.abs(diff) > 40) {
      if (diff < 0) handleNext();
      else handlePrev();
    }
    touchStartX.current = null;
  };



  // Get visible avatars (7 total: 3 left, center, 3 right)
  const getVisibleAvatars = () => {
    if (avatars.length === 0) return [];
    const visible = [];
    for (let i = -3; i <= 3; i++) {
      const index = (currentIndex + i + avatars.length) % avatars.length;
      visible.push({
        ...avatars[index],
        position: i,
        actualIndex: index,
      });
    }
    return visible;
  };

  const visibleAvatars = getVisibleAvatars();
  const centerAvatar = avatars[currentIndex];

  if (avatars.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gradient-to-b from-[#2b3544] to-[#1e2633] rounded-lg">
        <p className="text-gray-400">No avatars available</p>
      </div>
    );
  }

  return (
    <div
      className="relative w-full bg-gradient-to-b pb-12 px-8 rounded-lg overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Avatar Carousel - central y laterales con slide */}
      <div className="relative h-40 flex items-center justify-center mb-8 overflow-x-hidden">
        <AnimatePresence initial={false} custom={direction}>
          <Motion.div
            key={currentIndex}
            className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center w-full"
            custom={direction}
            initial={{ x: direction > 0 ? 300 : -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: direction > 0 ? -300 : 300, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30, duration: 0.5 }}
            style={{ width: 480, height: 160 }}
          >
            {visibleAvatars.map((avatar) => {
              const absPos = Math.abs(avatar.position);
              let scale, opacity, zIndex, translateX;
              if (absPos === 0) {
                scale = 1;
                opacity = 1;
                zIndex = 50;
                translateX = avatar.position * 140;
              } else if (absPos === 1) {
                scale = 0.7;
                // Animación: si acaba de moverse, opacidad 0, luego 0.6 tras 1s
                opacity = 0.6;
                zIndex = 40;
                translateX = avatar.position * 100;
              } else if (absPos === 2) {
                scale = 0.5;
                opacity = 0.3;
                zIndex = 30;
                translateX = avatar.position * 130;
              } else {
                scale = 0.4;
                opacity = 0.15;
                zIndex = 20;
                translateX = avatar.position * 150;
              }
              const isCenter = avatar.position === 0;
              const isClickable = avatar.position !== 0;

              // Estado para controlar opacidad de los adyacentes
              const [adjOpacity, setAdjOpacity] = useState(opacity);
              useEffect(() => {
                if (absPos === 1) {
                  setAdjOpacity(0);
                  const timeout = setTimeout(() => setAdjOpacity(0.6), 200);
                  return () => clearTimeout(timeout);
                } else {
                  setAdjOpacity(opacity);
                }
              }, [currentIndex]);

              return (
                <Motion.div
                  key={`${avatar.id}-${avatar.position}`}
                  className="absolute"
                  style={{ zIndex }}
                  initial={false}
                  animate={{
                    scale,
                    opacity: absPos === 1 ? adjOpacity : opacity,
                    x: translateX,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                    duration: 0.5,
                  }}
                  whileHover={isClickable ? { scale: scale * 1.1 } : {}}
                  onClick={() => isClickable && handleAvatarClick(avatar.actualIndex)}
                >
                  <div
                    className={`rounded-full overflow-hidden border-4 ${
                      isCenter
                        ? "border-white shadow-2xl shadow-blue-500/50"
                        : "border-gray-600/50"
                    } ${isClickable ? "cursor-pointer" : ""}`}
                    style={{
                      width: isCenter ? "120px" : `${120 * scale}px`,
                      height: isCenter ? "120px" : `${120 * scale}px`,
                    }}
                  >
                    <img
                      src={avatar.src}
                      alt={avatar.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </Motion.div>
              );
            })}
          </Motion.div>
        </AnimatePresence>
      </div>

      {/* Center Avatar Info */}
      {centerAvatar && (
        <Motion.div
          key={currentIndex}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <h3 className="text-white text-xl font-bold mb-1">{centerAvatar.name}</h3>
          <p className="text-gray-400 text-sm">{centerAvatar.role}</p>
        </Motion.div>
      )}

      {/* Navigation Buttons */}
      {avatars.length > 1 && (
        <>
          {/* Left Button */}
          <Motion.button
            onClick={handlePrev}
            className="opacity-0 sm:opacity-20 sm:hover:opacity-90 duration-300 absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-3 transition-colors z-50"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Previous avatar"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </Motion.button>

          {/* Right Button */}
          <Motion.button
            onClick={handleNext}
            className="opacity-0 sm:opacity-20 sm:hover:opacity-90 duration-300 absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-3 transition-colors z-50"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Next avatar"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Motion.button>
        </>
      )}

      {/* Rotation Indicator */}
      {autoRotate && !isPaused && (
        <div className="opacity-0 absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {avatars.map((_, index) => (
            <Motion.div
              key={index}
              className={`h-2 rounded-full transition-all ${
                index === currentIndex ? "w-8 bg-white" : "w-2 bg-white/30"
              }`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: index * 0.1 }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
